/**
 * Display formatting shared by the dashboard, Reports and the insights modal,
 * so the same figure reads the same way on every screen.
 */
import { formatNumber } from '../theme';

const wholePounds = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
});

export function formatPounds(value: number): string {
  return wholePounds.format(Math.round(value));
}

export function formatCount(value: number): string {
  return formatNumber(Math.round(value));
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatShare(part: number, total: number): string {
  // A missing count reads as 0%, never "NaN%".
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return '0%';
  const pct = (part / total) * 100;
  return pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
}

/** Hour-of-day label: 12am, 9am, 1pm. */
export function formatHour(hour: number): string {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

/**
 * The server labels days "Apr 7", which is the American order. Flip it to
 * "7 Apr" here rather than in the server, which mobile also reads.
 */
export function ukDayLabel(label: string): string {
  const m = /^([A-Z][a-z]{2}) (\d{1,2})$/.exec(label);
  return m ? `${m[2]} ${m[1]}` : label;
}

export function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s;
}


/**
 * How long ago a server timestamp was: "just now", "5m ago", "2h ago",
 * "3d ago", then the date. ClickHouse and SQLite both hand back UTC without
 * a zone ("2026-10-14 09:00:00"), so a bare timestamp is read as UTC.
 */
export function relativeTime(raw: string, now: Date = new Date()): string {
  if (!raw) return '';
  let s = raw.trim().replace(' ', 'T');
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return raw;
  const secs = Math.round((now.getTime() - t.getTime()) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', timeZone: 'Europe/London',
    ...(t.getUTCFullYear() !== now.getUTCFullYear() ? { year: 'numeric' as const } : {}),
  }).format(t);
}

/** An ISO date, "2026-10-11", as "11 Oct". Anything else passes through. */
export function isoDayLabel(label: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (!m) return label;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
}
